/**
 * dexScrollCoverage — b18g.
 *
 * Pure helpers for detecting scroll gaps (rows skipped between consecutive
 * OCR frames) and computing post-scan coverage (dex rows never read).
 *
 * A "row" is a 4-cell horizontal band in the PoGo Pokedex grid. The row
 * index for a national-dex ID is floor((id - 1) / 4), so dex IDs 1-4 are
 * row 0, IDs 5-8 are row 1, etc. "Consecutive frames jump row indices > 1"
 * means the minimum row index seen in frame N+1 is more than 1 above the
 * maximum row index seen in frame N.
 *
 * All functions are pure and total; they hold no state. The caller
 * (dexLiveScanSession / useDexLiveScan) maintains the frame history.
 */
import type { PokedexRegionLite } from "./pogoPokedexVariantClassifier";
import { REGION_META } from "./topPanelOcr";

/** Number of cells in one PoGo Pokedex grid row. */
export const GRID_COLS = 4;

/** Convert a national-dex ID to its 0-based grid-row index (IDs 1-4 -> row 0). */
export function dexIdToRow(id: number): number {
  return Math.floor((id - 1) / GRID_COLS);
}

/**
 * All national-dex IDs that belong to a given 0-based row index,
 * clamped to [1, maxId].
 */
export function rowToDexIds(row: number, maxId = 151): number[] {
  const first = row * 4 + 1;
  const last = Math.min(first + 3, maxId);
  if (first > maxId) return [];
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

/**
 * Describes a gap between two consecutive OCR frames.
 *
 * `fromRow`  — the max row observed in the earlier frame.
 * `toRow`    — the min row observed in the later frame.
 * `skipped`  — the count of row indices between them (exclusive): toRow - fromRow - 1.
 */
export interface RowGap {
  /** 0-based row index of the last row seen in the earlier frame. */
  fromRow: number;
  /** 0-based row index of the first row seen in the later frame. */
  toRow: number;
  /** Number of row indices that were completely skipped (>= 1). */
  skipped: number;
}

/**
 * Detect row-index jumps across a sequence of per-frame ID arrays.
 *
 * Each element of `framesReadIds` is the set of national-dex IDs seen in
 * one OCR frame (in arrival order, oldest first). The function returns a
 * `RowGap` for every pair of consecutive frames where the row-index jump
 * exceeds `minGap` (default 1 -- i.e. a gap of >= 2 row indices between
 * the top of one frame and the bottom of the previous frame).
 *
 * Frames with no IDs are skipped when computing the previous-frame
 * baseline so a dropped/empty frame doesn't falsely report a gap.
 */
export function detectScrollGaps(
  framesReadIds: number[][],
  opts?: { minGap?: number },
): RowGap[] {
  const minGap = opts?.minGap ?? 1;
  const gaps: RowGap[] = [];
  let prevMaxRow: number | null = null;

  for (const ids of framesReadIds) {
    if (ids.length === 0) continue;
    const rows = ids.map(dexIdToRow);
    const minRow = Math.min(...rows);
    const maxRow = Math.max(...rows);

    if (prevMaxRow !== null) {
      const skipped = minRow - prevMaxRow - 1;
      if (skipped >= minGap) {
        gaps.push({ fromRow: prevMaxRow, toRow: minRow, skipped });
      }
    }

    prevMaxRow = maxRow;
  }

  return gaps;
}

/**
 * Compute the set of national-dex IDs that were never seen across all
 * OCR frames.
 *
 * `allSeenIds`   — flat union of every ID read this session.
 * `expectedIds`  — the full set of IDs the session was expected to cover
 *                  (e.g. all Kanto IDs 1-151). When omitted, the expected
 *                  range is derived as [min(allSeenIds), max(allSeenIds)].
 *
 * Returns a sorted array of IDs that fall within the expected range but
 * were never observed.
 */
export function computeMissingIds(
  allSeenIds: number[],
  expectedIds?: number[],
): number[] {
  if (allSeenIds.length === 0 && !expectedIds) return [];

  const seen = new Set(allSeenIds);

  let expected: number[];
  if (expectedIds) {
    expected = expectedIds;
  } else {
    const minId = Math.min(...allSeenIds);
    const maxId = Math.max(...allSeenIds);
    expected = Array.from({ length: maxId - minId + 1 }, (_, i) => minId + i);
  }

  return expected.filter((id) => !seen.has(id)).sort((a, b) => a - b);
}

/**
 * Count the number of distinct grid rows missing from coverage.
 * Two consecutive missing IDs in the same row count as one missing row.
 */
export function countMissingRows(missingIds: number[]): number {
  if (missingIds.length === 0) return 0;
  const rows = new Set(missingIds.map(dexIdToRow));
  return rows.size;
}

/**
 * PR2: Compute the set of FULLY-MISSED grid rows (1-based) for one variant.
 *
 * A row owns up to GRID_COLS in-range expected IDs. It counts as fully missed
 * ONLY IF every one of its expected (in-range) IDs is absent from `seenIds`.
 * A row with even a single seen cell is NOT reported. A partial last row
 * (fewer than GRID_COLS expected IDs in range) is fully missed only if all of
 * its in-range expected IDs are unseen.
 *
 * Rows are REGION-RELATIVE: the PoGo Pokedex grid restarts at each region's first
 * national-dex id (Kanto #1, Johto #152, Hoenn #252 …), so a row is bucketed by
 * its offset from the region's first id, not the global `floor((id-1)/4)`. That
 * makes the reported row align with the on-screen grid — Johto's first row is
 * #152–155, not the global #153–156 that split it across the Kanto grid. The
 * anchor is the smallest expected id (expectedIdsForRegion returns one region's
 * contiguous range). 1-based row numbers, restarting at 1 for each region.
 */
export function fullyMissedRows(
  seenIds: number[],
  expectedIds: number[],
  cols: number = GRID_COLS,
): number[] {
  if (expectedIds.length === 0) return [];
  const seen = new Set(seenIds);
  const width = Math.max(1, cols);
  const rowExpected = new Map<number, number[]>();
  // ORDINAL, not arithmetic. See rowsForRoster below for why.
  expectedIds.forEach((id, i) => {
    const row0 = Math.floor(i / width);
    const group = rowExpected.get(row0);
    if (group) group.push(id);
    else rowExpected.set(row0, [id]);
  });
  const missed: number[] = [];
  rowExpected.forEach((ids, row0) => {
    if (ids.every((id) => !seen.has(id))) missed.push(row0 + 1);
  });
  return missed.sort((a, b) => a - b);
}

/**
 * Split an ordered roster into its on-screen rows.
 *
 * WHY ORDINAL POSITION, NOT DEX ARITHMETIC (2026-07-23). This used to bucket by
 * `floor((id - anchor) / 4)`, which silently assumes the dex is a CONTIGUOUS
 * run of national-dex ids four wide. That holds for the main dex and is wrong
 * for every curated one. The Mega dex jumps 0003 → 0006 → 0009 → 0015; treating
 * that as a numeric span invents ~10 phantom cells per row, so every reported
 * row on Mega, G-Max, Shadow and Purified was meaningless — the user's report.
 *
 * A row is simply the Nth chunk of the dex's OWN ordered roster. Passing a
 * contiguous region range reproduces the old arithmetic exactly (index i and
 * id-anchor coincide), so the main dex is unchanged by construction.
 *
 * Column width is per-dex and already known to the scanner: G-Max renders 3
 * wide, everything else 4 (`gridColumnsForVariant` in dexOcrRoi). Verified
 * against device screenshots on 2026-07-23 — Kanto Mega is 19 entries in 5 rows
 * of 4, Kanto G-Max is 12 entries in 4 rows of 3.
 *
 * Because rows follow the roster, this needs no maintenance as Niantic adds
 * Pokémon: a new Mega appended to the roster re-flows the rows automatically.
 *
 * MULTI-FORM SPECIES: a species GO lists as two cells sharing one dex number
 * (Mega Charizard/Mewtwo/Raichu X&Y; G-Max Urshifu's two styles) occupies two
 * CELLS but appears once in the base roster. Pass the roster through
 * `expandRosterToCells` FIRST so those cells are present and the rows after them
 * line up with the grid. The base roster stays id-keyed (dexStateStore and the
 * silhouette atlas rely on that); expansion is a coverage-only view.
 */
export function rowsForRoster(roster: number[], cols: number): number[][] {
  const width = Math.max(1, cols);
  const rows: number[][] = [];
  for (let i = 0; i < roster.length; i += width) {
    rows.push(roster.slice(i, i + width));
  }
  return rows;
}

/**
 * Expand an ordered base-species roster into the on-screen CELL sequence,
 * repeating each species by however many grid cells it occupies.
 *
 * The count comes from a caller-supplied function (so this stays a pure util
 * with no roster/variant imports) — in practice `dexCellCountFor(variant, id)`
 * from megaGmaxSprites. A repeated id is emitted contiguously, matching how GO
 * renders X immediately before Y (0006 X, 0006 Y adjacent).
 *
 * The repeat is the SAME base id, so every downstream check stays base-keyed: a
 * row containing [6, 6] is "seen" iff #6 was seen, exactly as before. Only the
 * row BOUNDARIES change, which is the whole point — a two-cell species used to
 * push every later species half a cell out of its true row.
 */
export function expandRosterToCells(
  roster: number[],
  cellCountFor: (id: number) => number,
): number[] {
  const cells: number[] = [];
  for (const id of roster) {
    const n = Math.max(1, cellCountFor(id));
    for (let i = 0; i < n; i += 1) cells.push(id);
  }
  return cells;
}

/**
 * Dex-number span of a REGION-RELATIVE 1-based row, anchored at the region's
 * first national-dex id. Row 1 of Johto (first id #152) → { first: 152, last: 155 }.
 * Region-agnostic on the high end (no clamp) — the row's full 4-cell span, matching
 * how the grid renders a trailing row.
 */
export function regionRowDexRange(
  row1Based: number,
  regionFirstId: number,
): { first: number; last: number } {
  const first = regionFirstId + (row1Based - 1) * GRID_COLS;
  return { first, last: first + GRID_COLS - 1 };
}

/**
 * b18-coverage-fix: Return the full array of national-dex IDs that belong to
 * the given region, using the canonical REGION_META range table from
 * topPanelOcr. Returns all IDs 1-1025 for "unknown" as a safe fallback.
 */
export function expectedIdsForRegion(region: PokedexRegionLite): number[] {
  if (region === "unknown") {
    return Array.from({ length: 1025 }, (_, i) => i + 1);
  }
  const meta = REGION_META[region];
  const [start, end] = meta.range;
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/**
 * b19 (B19-4 telemetry-first): Describe a contiguous band of missing dex
 * IDs as a half-open run [start, end] (both inclusive).
 *
 * Example: missingIds = [1,2,3,4,5,6,7,8, 13,14,15,16] yields
 * bands = [{start:1,end:8}, {start:13,end:16}].
 */
export interface RecallGapBand {
  /** Lowest dex id in the contiguous missing run (inclusive). */
  start: number;
  /** Highest dex id in the contiguous missing run (inclusive). */
  end: number;
}

/**
 * b19 (B19-4 telemetry-first): Collapse a sorted-or-unsorted array of
 * missing dex IDs into contiguous bands. Pure; preserves order ascending.
 * Empty input yields []. Duplicates are de-duped before banding.
 */
export function computeRecallGapBands(missingIds: number[]): RecallGapBand[] {
  if (missingIds.length === 0) return [];
  const sorted = Array.from(new Set(missingIds)).sort((a, b) => a - b);
  const bands: RecallGapBand[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const id = sorted[i];
    if (id === prev + 1) {
      prev = id;
      continue;
    }
    bands.push({ start, end: prev });
    start = id;
    prev = id;
  }
  bands.push({ start, end: prev });
  return bands;
}

/**
 * b19 (B19-4 telemetry-first): Classify a scan's final coverage state for
 * telemetry. Pure.
 *
 * - "complete": no bands missing.
 * - "scrolled_too_fast": >= 2 bands missing (multiple gaps means user
 *    scrolled past several rows the scanner could not catch up to).
 * - "incomplete_scroll": exactly 1 band missing (user stopped before
 *    completing the region, or skipped one contiguous chunk).
 */
export type RecallGapFinalState =
  | "complete"
  | "incomplete_scroll"
  | "scrolled_too_fast";

export function classifyRecallGapFinalState(
  bands: RecallGapBand[],
): RecallGapFinalState {
  if (bands.length === 0) return "complete";
  if (bands.length === 1) return "incomplete_scroll";
  return "scrolled_too_fast";
}
